import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * Checkbox — shadcn idiom on `@radix-ui/react-checkbox`, themed onto HofSync
 * brand tokens. Radix renders a native `<button role="checkbox">` (plus a hidden
 * form-bubble input only when mounted inside a `<form>`), so — because the DS
 * Tailwind build deliberately excludes preflight (see CLAUDE.md) — the native
 * button keeps its UA chrome unless we neutralise it per-component, exactly as
 * `input.tsx` / `button.tsx` do:
 *   - `tw:appearance-none` removes UA control styling,
 *   - an explicit `tw:border-2 tw:border-input` + `tw:bg-card` re-establish the box
 *     on the brand `--input` / `--card` tokens,
 *   - `tw:focus-visible:outline-none` + `tw:ring-2 tw:ring-ring` swap the UA focus
 *     outline for the brand ring (`--ring`),
 *   - `tw:font-sans` resets the UA font-family — form controls do NOT inherit the
 *     document font, so without it the `<button>` keeps the UA control font (#1927).
 * The ON / indeterminate box paints with the ACKERDOKU brand
 * (`tw:data-[state=checked]:bg-primary` / `data-[state=indeterminate]`); the
 * indicator shows a Minus for the indeterminate ("select all" partial) state and
 * a Check otherwise, mirroring the legacy molecule this primitive now backs.
 *
 * DS-INTERNAL: intentionally NOT re-exported from the shadcn barrel. The public
 * `Checkbox` name is owned by the legacy `molecules/Checkbox`; flat-exporting a
 * second `Checkbox` would TS2308-break the barrel (rule in CLAUDE.md). The
 * molecule is rebuilt on this primitive with its public API unchanged, so the
 * barrel export question dissolves.
 *
 * `@radix-ui/react-checkbox` is a real `dependency` (the DS is consumed as source),
 * vendored under the copy-in / ownership policy in CLAUDE.md.
 */
export type CheckboxProps = React.ComponentPropsWithoutRef<
  typeof CheckboxPrimitive.Root
>;

export const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      'tw:peer tw:inline-flex tw:h-5 tw:w-5 tw:shrink-0 tw:appearance-none tw:items-center tw:justify-center tw:rounded-[4px] tw:border-2 tw:border-input tw:bg-card tw:font-sans tw:transition-colors',
      'tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring',
      'tw:enabled:hover:border-primary',
      'tw:disabled:cursor-not-allowed tw:disabled:opacity-50',
      'tw:data-[state=checked]:border-primary tw:data-[state=checked]:bg-primary tw:data-[state=checked]:text-primary-foreground',
      'tw:data-[state=indeterminate]:border-primary tw:data-[state=indeterminate]:bg-primary tw:data-[state=indeterminate]:text-primary-foreground',
      className
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className="tw:flex tw:items-center tw:justify-center tw:text-current">
      {props.checked === 'indeterminate' ? (
        <Minus className="tw:h-3.5 tw:w-3.5" strokeWidth={3} />
      ) : (
        <Check className="tw:h-3.5 tw:w-3.5" strokeWidth={3} />
      )}
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = 'Checkbox';
